import { useGlobalContainerRef } from '@/wc/helper/ref';
import { Tag, TagProps, Tooltip } from '@moneyforward/ap-frontend-components';
import type { ErrorWithLabelMessage } from '@/context/services/admin_invoice_reports/AdminInvoiceReportsQuery.enums';
import classnames from 'classnames/bind';
import { FC, memo } from 'react';
import { useInfoTagCell } from '../../hooks/useInfoTagCell';
import styles from './InfoTagCell.module.scss';

const cx = classnames.bind(styles);

export type InfoTagCellProps = {
  errorMessages?: ErrorWithLabelMessage[];
  messageType: string[];
  receiptType?: string;
  children?: React.ReactNode;
};

export const InfoTagCell: FC<InfoTagCellProps> = memo(
  ({ errorMessages, messageType, receiptType, children }) => {
    const container = useGlobalContainerRef();
    const { filteredMessages } = useInfoTagCell({ errorMessages, messageType });

    const hasErrorMessages = filteredMessages.length > 0;
    const hasReceiptType = !!receiptType;

    if (!hasErrorMessages && !hasReceiptType) {
      return <>{children}</>;
    }

    return (
      <div className={cx('infoTagCell')}>
        <div>{children}</div>
        <div className={cx('tags')}>
          {!hasErrorMessages && hasReceiptType && (
            <div className={cx('tagContainer')}>
              <Tag className={cx('tag')} closable={false} outline={false}>
                {receiptType}
              </Tag>
            </div>
          )}

          {filteredMessages.map((msg, index) => {
            const status: TagProps['status'] =
              msg.type === 'warn' ? 'warning' : 'error';

            return (
              <Tooltip
                key={`${msg.message_type}-${index}`}
                title={msg.message}
                placement='top'
                overlayClassName={
                  msg.message_type === 'duplicate_reports_message'
                    ? cx('duplicateReportsTooltipContainer')
                    : cx('tooltipContainer')
                }
                getPopupContainer={container}
              >
                <div className={cx('tagContainer')}>
                  <Tag
                    className={cx('tag', status)}
                    closable={false}
                    outline={false}
                    status={status}
                    icon
                  >
                    {msg.label}
                  </Tag>
                </div>
              </Tooltip>
            );
          })}
        </div>
      </div>
    );
  }
);

InfoTagCell.displayName = 'InfoTagCell';
